<template>
  <div class="available-claim" :class="{ 'highlight-is-mine': isMineRecipe }">
    <div class="available-claim-avatar" :class="{ 'available-claim-avatar-mt': isMineRecipe }">
      <el-tooltip
        v-if="runningRecipe?.userAvatarUrl && runningRecipe?.userName"
        trigger="hover"
        popper-class="popper-default"
        :content="isAddress(formattedUserName) ? formatAddress(formattedUserName) : formattedUserName"
        placement="top"
      >
        <template #default>
          <el-avatar
            class="avatar-pointer"
            :class="{ 'avatar-is-mine': isMineRecipe }"
            :src="runningRecipe?.userAvatarUrl"
            :size="38"
          />
        </template>
      </el-tooltip>
    </div>
    <div class="available-claim-content">
      <div class="available-claim-info">
        <div v-if="isSingleProductRecipe" class="recipe-row-token-info">
          <img :src="getIcon(Number(recipeId), recipe?.products[0].tokenAddress || '')" :alt="tokenMetadata.label" />
          <h3>({{ runningRecipe.multiplyModifier }})</h3>
        </div>
        <div v-else class="recipe-row-token-info">
          <img :src="getIcon(Number(recipeId), recipe?.products[0].tokenAddress || '')" :alt="tokenMetadata.label" />
          <h3>({{ runningRecipe.multiplyModifier }})</h3>
        </div>

        <div class="recipe-row-token-info">
          <vue-countdown
            v-slot="{ days, hours, minutes, seconds }"
            :time="countdownTimestamp"
            :class="{ 'countdown-is-mine': isMineRecipe }"
            @end="endTimerHandler"
            @progress="
              ({ totalMilliseconds }) => {
                percent = 100 - (totalMilliseconds / (runningRecipe.craftDuration * 1000)) * 100;
                timerTokensForClaimRef = Math.floor(
                  calculateTokensAvailable(
                    totalMilliseconds,
                    runningRecipe.craftDuration * 1000,
                    runningRecipe.multiplyModifier
                  )
                );
              }
            "
          >
            <template v-if="BigNumber(days).isGreaterThan(0)">
              {{ Formatters.text.zeroPrefix(days) + t('coreDaysPostfix') }}
              {{ BigNumber(hours).isGreaterThan(0) ? Formatters.text.zeroPrefix(hours) + t('coreHoursPostfix') : '' }}
            </template>

            <template v-else-if="BigNumber(hours).isGreaterThan(0)">
              {{ Formatters.text.zeroPrefix(hours) + t('coreHoursPostfix') }}
              {{ BigNumber(minutes).isGreaterThan(0) ? Formatters.text.zeroPrefix(minutes) + t('coreMinPostfix') : '' }}
            </template>

            <template v-else-if="BigNumber(minutes).isGreaterThan(0)">
              {{ Formatters.text.zeroPrefix(minutes) + t('coreMinPostfix') }}
              {{ BigNumber(seconds).isGreaterThan(0) ? Formatters.text.zeroPrefix(seconds) + t('coreSecPostfix') : '' }}
            </template>

            <template v-else>
              {{ Formatters.text.zeroPrefix(seconds) + t('coreSecPostfix') }}
            </template>
          </vue-countdown>
        </div>
      </div>

      <div class="available-claim-amount-bar-container">
        <div class="available-claim-amount-container">
          <span :class="isMineRecipe ? 'available-claim-amount-is-mine' : 'available-claim-amount-is-not-mine'">{{
            timerTokensForClaimRef
          }}</span>
          <span>/{{ runningRecipe.multiplyModifier }}</span>
        </div>
        <div
          :class="[
            'available-claim-progress',
            percent <= CRAFTING_PERCENT ? 'default' : 'halfDone',
            !isMineRecipe && 'notMine'
          ]"
        >
          <div
            :class="[
              'available-claim-progress-fill',
              percent <= CRAFTING_PERCENT ? 'default' : 'halfDone',
              !isMineRecipe && 'notMine'
            ]"
            :style="{
              width: `${percent}%`
            }"
          />
        </div>
      </div>
      <div v-if="isMineRecipe && recipe?.products && !isSingleRecipe" class="available-claim-container">
        <div class="btn-outline">
          <div v-for="product of recipe.products" :key="product.tokenAddress" class="btn-outline-block">
            <img
              :src="
                getIcon(
                  Number(recipeId),
                  product.tokenAddress || '',
                  product.nftValueMax && recipe.products.length === 1 ? false : true
                )
              "
              :alt="tokenMetadata.label"
            />
            <div v-if="isNft" class="available-claim-item">
              <span class="available-claim-item-counter">{{ unclaimedModifier }}</span>
            </div>
            <span
              v-else-if="new BigNumber(product.quantityMin).eq(product.quantityMax)"
              class="available-claim-item-counter"
            >
              {{ unclaimedModifier }}
            </span>
            <span v-else class="available-claim-item-counter">
              {{ new BigNumber(product.quantityMin).decimalPlaces(1).multipliedBy(unclaimedModifier) }}-{{
                new BigNumber(product.quantityMax).decimalPlaces(1).multipliedBy(unclaimedModifier)
              }}
            </span>
          </div>
        </div>
        <button
          class="btn-primary"
          type="button"
          :disabled="
            unclaimedModifier <= 0 ||
            isLoadingClaimButton ||
            isClaimingValue ||
            isClaimButtonLoading ||
            store.isLoadingUnclaimedModifiers
          "
          @click="claimCraftedTokens(runningRecipe.userRecipeIndex)"
        >
          <span>{{ $t('craftingClaim') }} </span>
          <img src="/img/icons/chevronRight.svg" alt="chevron right" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Crafting__factory } from 'public/abi/evm/types';
import { ElNotification } from 'element-plus';
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import { BigNumber } from 'bignumber.js';
import VueCountdown from '@chenfengyuan/vue-countdown';
import useProvider from '~/composables/useProvider';
import { type TTokensConfigItem, CRAFTING_PERCENT } from '~/utils/constants';
import type { RunningRecipeProps, RecipeProps, CraftAvailableForClaim } from '~/types/crafting';
import { calculateTimeLeftPercent } from '~/utils/crafting';
import { getAddress, ZeroAddress } from 'ethers';
import { Formatters } from '~/utils/formatters/formatters';

const { t } = useI18n();
const { getProvider } = useProvider();
const { address } = useWeb3ModalAccount();
const { sendContractMethod } = useSendContractMethod();
const { blockchain, apiUrl } = useEnvs();
const store = useMainStore();
const { callBalanceUpdate } = useHalloweenStore();

const isClaiming = inject<Ref<boolean>>('isClaiming');
const isClaimingValue = computed(() => isClaiming?.value || false);

const unclaimedModifier = computed(() => store.getUnclaimedModifier(runningRecipe));

const { tokenMetadata, runningRecipe, recipeId, isClaimButtonLoading } = defineProps<{
  tokenMetadata: TTokensConfigItem;
  runningRecipe: RunningRecipeProps;
  recipeId: string;
  isClaimButtonLoading: boolean;
}>();

const emit = defineEmits<{
  claimLoadStateChanged: [isLoading: boolean];
}>();

const { data: recipe } = await useFetch<RecipeProps>(() => `/crafting/recipes/${recipeId}`, {
  baseURL: apiUrl,
  watch: [() => recipeId]
});

const endTimerHandler = () => {
  refreshNuxtData('recipes/in-queue');
};

const isNft = computed(() => tokenMetadata.interface === 'erc721');
const endTime = computed(() => new Date(runningRecipe.craftEndTime).getTime());
const startTime = computed(() => new Date(runningRecipe.craftStartTime).getTime());
const isSingleRecipe = computed(() => runningRecipe.multiplyModifier === 1);
const isSingleProductRecipe = computed(() => recipe.value?.products.length === 1);

const calculateTokensAvailable = (timeRemaining: number, totalDuration: number, totalTokens: number) => {
  const elapsedTime = totalDuration - timeRemaining;

  const durationPerToken = totalDuration / totalTokens;

  return Math.floor(elapsedTime / durationPerToken);
};

const formattedUserName = computed(() => {
  const rawName = runningRecipe?.userName ?? '';
  return with0xIfAddress(rawName);
});

const percent = ref<number>(calculateTimeLeftPercent(startTime.value, endTime.value, Date.now()));
const countdownTimestamp = computed(() => endTime.value - Date.now());
const timerTokensForClaimRef = ref<number>(0);
const isLoadingClaimButton = ref<boolean>(false);
const isMineRecipe = computed(
  () => getAddress(runningRecipe?.userAddress) === getAddress(address.value || ZeroAddress)
);

// Watch for local loading state changes and emit to parent
watch(isLoadingClaimButton, (newIsLoadingClaim) => emit('claimLoadStateChanged', newIsLoadingClaim));

watch(timerTokensForClaimRef, (newVal, oldVal) => {
  if (newVal > 0 && newVal > oldVal) {
    store.updateVersion();
  }
});

const claimCraftedTokens = async (id: number) => {
  isLoadingClaimButton.value = true;

  // Store the claimed recipe ID for an optimistic UI update
  const claimedRecipeId = BigNumber(recipeId).toString();

  try {
    const claimTx = await sendContractMethod({
      contract: Crafting__factory.connect(blockchain.contracts.crafting, getProvider()),
      methodName: 'claimRecipe',
      args: [recipeId, id]
    });

    if (!claimTx?.hash) return;
    const txReceipt = await claimTx?.wait();
    if (txReceipt) {
      saveHashToLocalStorage(`${t('claimingRecipesNotification')}`, claimTx.hash);

      // Optimistic UI update: Remove claimed recipe index from store
      const updatedClaimItems: CraftAvailableForClaim[] = [];

      store.cachedClaimItems.forEach((item) => {
        if (BigNumber(item.recipe.recipeId).eq(claimedRecipeId)) {
          // Remove the claimed recipe index from this item
          const updatedUnclaimedByRecipeIndexes = { ...item.unclaimedByRecipeIndexes };
          delete updatedUnclaimedByRecipeIndexes[id];

          // If there are still unclaimed indexes, keep the item
          if (Object.keys(updatedUnclaimedByRecipeIndexes).length > 0) {
            updatedClaimItems.push({
              ...item,
              unclaimedByRecipeIndexes: updatedUnclaimedByRecipeIndexes
            });
          }
        } else {
          // Keep items that weren't claimed
          updatedClaimItems.push(item);
        }
      });

      store.setCachedClaimItems(updatedClaimItems);

      // Update unclaimedModifiers: Remove the claimed recipe index
      const updatedUnclaimedModifiers = { ...store.unclaimedModifiers };
      delete updatedUnclaimedModifiers[id];
      store.setUnclaimedModifiers(updatedUnclaimedModifiers);
    }

    store.updateVersion();

    modifyHistoryHashStatus(claimTx.hash, 'Done');
    callBalanceUpdate();
    store.updateVersion();
  } catch (error) {
    console.error(error, 'error on claim');

    ElNotification.error({
      title: '',
      message: 'Error while claiming crafted token'
    });
  } finally {
    isLoadingClaimButton.value = false;
  }
};
</script>

<style lang="scss" scoped>
.available-claim {
  padding: 9px 20px 12px;
  border: 1px solid #1e353e4d;
  display: flex;

  @media screen and (max-width: 479px) {
    padding-right: 10px;
    padding-left: 10px;
  }

  &-avatar-mt {
    @media screen and (max-width: 767px) {
      margin-top: 21px;
    }
  }

  &-content {
    flex: 1;
  }

  &-info {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-left: 10px;

    img {
      height: 40px;
      width: 40px;
    }

    h3 {
      font-family: 'Grenze';
      font-weight: 600;
      line-height: 18px;
      text-align: left;
      color: #fff;

      @media screen and (max-width: 767px) {
        font-size: 16px;
      }
    }

    p {
      font-family: Grenze;
      font-size: 24px;
      font-weight: 500;
      line-height: 35.52px;
      text-align: left;
      color: #fff;
    }
  }

  &-progress {
    width: 100%;
    position: relative;
    border-radius: 8px;
    border: 1px solid #49ddaa;
    border-radius: 8px;
    height: 8px;
    overflow: hidden;
    display: flex;
    justify-content: space-evenly;
    transition: all 0.3s ease;
    background: linear-gradient(0deg, #010303, #17343e);

    &.notMine {
      border-color: #10364a;
    }

    &-bar {
      height: 100%;
      width: 2px;
      background: #fff;
      opacity: 0.2;
      position: relative;
      z-index: 2;
      transition: all 0.3s ease;
    }

    &-fill {
      height: 100%;
      width: 70%;
      position: absolute;
      top: 0;
      left: 0;

      background: linear-gradient(90deg, #01c0fe 0%, #60ff7c 100%);
      clip-path: polygon(0 0, 100% 0, calc(100% - 3px) 100%, 0 100%);
      will-change: transform;

      &.notMine {
        background: linear-gradient(90deg, #c9c9c9 0%, rgba(201, 201, 201, 0.5) 99.89%);
      }
    }
  }

  &-container {
    display: flex;
    justify-content: flex-end;
    align-items: center;

    @media screen and (max-width: 400px) {
      flex-direction: column;
      align-items: flex-end;
      gap: 10px;
    }

    .btn-primary {
      margin-left: 10px;
      display: flex;
      align-items: center;

      span {
        word-wrap: normal;
      }

      @media screen and (max-width: 767px) {
        line-height: 0px;
        margin-left: 8px;
        padding-left: 5px;
        padding-right: 5px;
        width: fit-content;
      }
    }

    .btn-outline {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: row;
      gap: 10px;
      border-color: rgba(201, 201, 201, 0.5);
      min-width: fit-content;
      background: radial-gradient(circle, rgba(4, 15, 20, 1) 0%, rgba(8, 27, 37, 1) 100%);
      color: #49ddaa;
      pointer-events: none;
      user-select: none;
      padding: 2px 10px;

      @media screen and (max-width: 767px) {
        width: fit-content;
      }

      &-block {
        display: flex;
        align-items: center;

        span {
          padding-left: 3px;
          word-wrap: normal;
        }
      }

      img {
        height: 40px;
        min-height: 40px;
        min-width: 40px;
      }

      .available-claim-item {
        display: flex;
        justify-content: center;
        align-items: center;

        &:last-child {
          padding-right: 5px;
        }
      }

      .available-claim-item-counter {
        font-size: 20px;
      }
    }
  }

  &-amount-bar-container {
    display: flex;
    gap: 20px;
    align-items: center;
    margin: 4px 0 10px;
  }

  &-amount-container {
    margin-left: 10px;
    font-weight: 600;
    font-size: 18px;
    text-align: center;
    vertical-align: middle;
    color: #fff;

    @media screen and (max-width: 767px) {
      font-size: 16px;
    }
  }

  &-amount-is-mine {
    color: #49ddaa;
  }

  &-amount-is-not-mine {
    color: #fff;
  }
}

.countdown-is-mine {
  color: #49ddaa;
}

.highlight-is-mine {
  background: radial-gradient(59.02% 71.08% at 84.01% 85.82%, #040f14 0%, #081b25 100%);
  box-shadow: 2px 4px 5px 0px #00000040;
}

.avatar-pointer {
  cursor: pointer;
}

.avatar-is-mine {
  box-shadow: 0px 0px 5px 0px #ddcead;
}
</style>
